import { useEffect, useState } from "react";
import {
  DataRoomContext,
  DataRoomFileQuery,
  DataRoomPermission,
  useDataRoomFastQuery,
  useDataRoomFileQuery,
  useUpdateDataRoomFileMutation,
} from "../../graphql/generated";
import useGqlClient from "../../hooks/useGqlClient";
import { AnimatedModal } from "../AnimatedModal";
import { CloseIcon } from "../CloseIcon";
import { H3 } from "../Heading";
import Loading from "../Loading";
import { TextInput } from "../tailwind/TextInput";
import { TextArea } from "../tailwind/TextArea";
import Dropdown, { Option } from "../tailwind/Dropdown";
import { Button } from "../tailwind/Button";
import { useParams } from "react-router-dom";
import { useInvalidateQueryKeys } from "@/src/hooks/useInvalidateQueryKeys";

interface Props {
  open: boolean;
  onClose: () => void;
  onUpdated?: () => void;
  dataRoomFileId: string;
}

export function EditFile(props: Props) {
  const client = useGqlClient();
  const { dealId: activeDealId } = useParams<{ dealId: string }>();

  const [fileId, setFileId] = useState(props.dataRoomFileId);
  useEffect(() => {
    if (props.dataRoomFileId) {
      setFileId(props.dataRoomFileId);
    }
  }, [props.dataRoomFileId]);

  const { data, isLoading, error } = useDataRoomFileQuery(
    client,
    {
      id: fileId,
    },
    {
      enabled: props.open && fileId !== "",
    },
  );

  const dataRoomQuery = useDataRoomFastQuery(
    client,
    {
      dealId: activeDealId ? activeDealId : "",
    },
    {
      enabled: props.open && !!activeDealId,
    },
  );

  return (
    <AnimatedModal open={props.open} onClose={props.onClose}>
      <EditFileHeader
        onClose={props.onClose}
        data={data}
        isLoading={isLoading || dataRoomQuery.isPending}
        error={error || dataRoomQuery.error}
      />
      <EditFileContent
        data={data}
        isLoading={isLoading || dataRoomQuery.isPending}
        error={error || dataRoomQuery.error}
        onClose={props.onClose}
        onUpdated={props.onUpdated}
        permissions={
          dataRoomQuery.data ? dataRoomQuery.data.deal.dataRoom.permissions : []
        }
      />
    </AnimatedModal>
  );
}

interface EditFileProps {
  isLoading: boolean;
  error: any;
  data: DataRoomFileQuery | undefined;
  onClose: () => void;
}

function EditFileHeader(props: EditFileProps) {
  let title = "Edit File";

  if (props.error) {
    title = "Error";
  }

  if (props.data) {
    title = `Edit ${props.data.dataRoomFile.name}`;
  }

  return (
    <div className="flex items-center justify-between">
      <H3>{title}</H3>
      <CloseIcon onClose={props.onClose} />
    </div>
  );
}

function EditFileContent(
  props: EditFileProps & {
    onUpdated?: () => void;
    permissions: Pick<DataRoomPermission, "id" | "name" | "description">[];
  },
) {
  const client = useGqlClient();
  const { invalidateDataRoomFolder, invalidateDataRoomFile } =
    useInvalidateQueryKeys();
  const [fileName, setName] = useState("");
  const [description, setDescription] = useState("");
  const [permission, setPermission] = useState<Option>({
    label: "Loading...",
    description: "...",
    value: "",
  });

  const updateDataRoomFile = useUpdateDataRoomFileMutation(client);

  useEffect(() => {
    if (props.data) {
      setName(props.data.dataRoomFile.name);
      setDescription(props.data.dataRoomFile.description);
      setPermission({
        value: props.data.dataRoomFile.permissions[0].id,
        label: props.data.dataRoomFile.permissions[0].name,
        description: props.data.dataRoomFile.permissions[0].description,
      });
    }
  }, [props.data, props.isLoading, props.error]);

  if (props.error) {
    return (
      <div className="flex justify-center items-center p-4">
        <p className="text-gray-700 font-bold">Something went wrong</p>
      </div>
    );
  }

  if (props.isLoading || !props.data) {
    return <Loading />;
  }

  return (
    <div>
      <form
        onSubmit={(e) => {
          e.preventDefault();

          if (!props.data) {
            return;
          }

          updateDataRoomFile.mutate(
            {
              input: {
                id: props.data.dataRoomFile.id,
                name: fileName,
                permissionIDs: permission.value ? [permission.value] : [],
                description,
              },
            },
            {
              onSuccess: () => {
                if (props.onUpdated) {
                  props.onUpdated();
                }

                if (props.data) {
                  invalidateDataRoomFile(props.data.dataRoomFile.id);
                  if (props.data.dataRoomFile.folderId) {
                    invalidateDataRoomFolder(props.data.dataRoomFile.folderId);
                  }
                }

                props.onClose();
              },
            },
          );
        }}
      >
        <TextInput
          margin="m 0 0 0"
          ignore1p
          label="Name"
          placeholder="Name of the file"
          value={fileName}
          onChange={(e) => setName(e.currentTarget.value)}
        />

        <p className="mt-3 block text-sm font-medium leading-6 text-gray-900">
          Description
        </p>
        <TextArea
          label="Description"
          rows={2}
          placeholder="A description of the file..."
          name="description"
          value={description}
          onChange={(e) => setDescription(e.currentTarget.value)}
        />

        {props.data.dataRoomFile.context === DataRoomContext.Deal ? (
          <div className="mt-3">
            <Dropdown
              label="Permission level"
              defaultOption={{
                label: props.permissions[0].name,
                description: props.permissions[0].description,
                value: props.permissions[0].id,
              }}
              selectedOption={permission ?? undefined}
              onSelect={(o) => {
                setPermission(o);
              }}
              options={props.permissions.map((p) => {
                return {
                  value: p.id,
                  label: p.name,
                  description: p.description,
                };
              })}
            />
          </div>
        ) : null}

        <div className="mt-12 flex justify-end">
          <Button
            type="submit"
            text="Save"
            variant="positive"
            isLoading={updateDataRoomFile.isPending}
            isDisabled={fileName === ""}
            loadingText="Saving..."
          />
        </div>
      </form>
    </div>
  );
}
